<script setup lang="ts">import { ref as _ref } from 'vue';

import { ActivitiesSearchForm } from "@/models/ActivitiesSearchForm";
import { fromUtc } from "@/services/UtilitiesService";
import { useRoute, useRouter } from "vue-router";
import { watch } from "vue";
import { omit } from "underscore";
import { activitiesService } from "@/services";
import { useBrandStore } from "@/stores/brand-store";
import RsPagination from "@/components/Pagination.vue";

import type Activity from "@/models/Activity";
import type { PagedData } from "@/models/PagedData";

withDefaults(defineProps<{
  brand: string;
  readyToLoad: boolean;
}>(), {  })

const $route = useRoute();
const $router = useRouter();

let searchForm = _ref(new ActivitiesSearchForm());
let hideForm = _ref(false);

let page = _ref(1);
let pageSize = _ref(10);
let activityType = _ref("");

let activitiesResult = _ref<PagedData<Activity[]> | null>(null);
let activityTotals = _ref<Record<string, number> | null>(null);

watch(
  $route,
  async (to) => {
    const selectedClient = useBrandStore().selectedClient;
    if (selectedClient == null) return;

    const query = to.query as Record<string, string>;
    const rest = omit(to.query, ["page", "pageSize", "activityType"]);

    activityTotals.value = await activitiesService.getActivityTotals(selectedClient, { brand: __props.brand, ...rest });

    if (query.activityType) {
      activitiesResult.value = await activitiesService.getActivities(selectedClient, { brand: __props.brand, ...query });
    } else activitiesResult.value = null;

    searchForm.value = { ...new ActivitiesSearchForm(), ...rest };
    page.value = +query.page || 1;
    pageSize.value = +query.pageSize || 10;
    activityType.value = query.activityType || "";
  },
  { immediate: true },
);

function updateActivityType(at: string) {
  activityType.value = at === activityType.value ? "" : at;
  doSearch(1, 10);
}

function doReset() {
  searchForm.value = new ActivitiesSearchForm();
  doSearch(1, 10);
}

function doSearch(p: number, ps: number) {
  const query = {
    caseReference: searchForm.value.caseReference || undefined,
    fileReference: searchForm.value.fileReference || undefined,
    additionalReference: searchForm.value.additionalReference || undefined,
    from: searchForm.value.from || undefined,
    to: searchForm.value.to || undefined,
    activityType: activityType.value || undefined,
    page: activityType.value ? `${p}` : undefined,
    pageSize: activityType.value ? `${ps}` : undefined,
  };
  $router.push({ name: "activities", query });
}
</script>
<template>
  <div class="tw-flex tw-h-full tw-items-stretch">
    <div class="case-menubar tw-flex tw-items-center tw-justify-between tw-bg-purple-900">
      <router-link :to="`/${brand}`">
        <img src="/logo-header.png" alt="Logo" />
      </router-link>
    </div>
    <div v-if="!readyToLoad" class="tw-mx-2 tw-my-1" :loading="!readyToLoad">Loading ...</div>
    <div v-else class="tw-m-2 tw-flex tw-w-full tw-flex-col">
      <div class="tw-b-r tw-m-2 tw-bg-gray-100 tw-p-2 tw-shadow">
        <div class="tw-my-1 tw-flex tw-items-center tw-justify-between">
          <h2 class="tw-col-span-2 tw-font-medium">Filters</h2>
          <button class="tw-col-span-1 tw-mx-3 tw-text-right" @click="hideForm = !hideForm">
            <i class="fa" :class="hideForm ? 'fa-chevron-down' : 'fa-chevron-up'" />
          </button>
        </div>
        <form v-show="!hideForm" class="tw-grid tw-grid-cols-3 tw-gap-2" @submit.prevent="doSearch(page, pageSize)">
          <div class="tw-flex tw-items-center tw-justify-end">
            <label for="brand-reference">Brand: </label>
            <div
              id="brand-reference"
              class="tw-b-r bg-gray-300 tw-mx-2 tw-box-content tw-w-32 tw-py-1 tw-pl-2 tw-pr-4"
              type="text"
            >
              {{ brand }}
            </div>
          </div>
          <div class="tw-flex tw-items-center tw-justify-end">
            <label for="case-reference">Case: </label>
            <input
              id="case-reference"
              v-model="searchForm.caseReference"
              class="tw-b-r tw-mx-2 tw-px-2 tw-py-1"
              type="text"
            />
          </div>
          <div class="tw-flex tw-items-center tw-justify-end">
            <label for="file-reference">File Reference: </label>
            <input
              id="file-reference"
              v-model="searchForm.fileReference"
              class="tw-b-r tw-mx-2 tw-px-2 tw-py-1"
              type="text"
            />
          </div>
          <div class="tw-flex tw-items-center tw-justify-end">
            <label for="additional-reference">Additional Reference: </label>
            <input
              id="additional-reference"
              v-model="searchForm.additionalReference"
              class="tw-b-r tw-mx-2 tw-px-2 tw-py-1"
              type="text"
            />
          </div>
          <div class="tw-flex tw-items-center tw-justify-end">
            <label for="from-date">From: </label>
            <input
              id="from-date"
              v-model="searchForm.from"
              class="tw-b-r tw-mx-2 tw-px-2 tw-py-1"
              type="date"
              :max="searchForm.to || new Date().toISOString().slice(0, 10)"
            />
          </div>
          <div class="tw-flex tw-items-center tw-justify-end">
            <label for="to-date">To: </label>
            <input
              id="to-date"
              v-model="searchForm.to"
              class="tw-b-r tw-mx-2 tw-px-2 tw-py-1"
              type="date"
              :min="searchForm.from"
              :max="new Date().toISOString().slice(0, 10)"
            />
          </div>
          <div class="tw-col-span-3 tw-flex tw-items-center tw-justify-end">
            <button class="tw-btn-md-white tw-b-r" type="reset" @click="doReset">Reset</button>
            <button class="tw-btn-md-gray tw-b-r tw-mx-2" type="submit">Filter</button>
          </div>
        </form>
      </div>
      <div v-if="activityTotals" class="tw-b-r tw-m-2 tw-grid tw-grid-cols-4 tw-gap-2 tw-bg-gray-100 tw-p-2 tw-shadow">
        <h2 class="tw-col-span-4 tw-font-medium">Overview</h2>
        <div
          v-for="total in Object.entries(activityTotals)"
          :key="total[0] + total[1]"
          class="cursor-pointer tw-overflow-hidden tw-rounded-lg tw-bg-white tw-shadow hover:tw-shadow-md"
          :class="total[0] === activityType ? 'tw-border tw-shadow-md' : ''"
          @click="updateActivityType(total[0])"
        >
          <div class="tw-p-3">
            <dl>
              <dt class="tw-truncate tw-font-medium tw-leading-5 tw-text-gray-600">
                {{ total[0] }}
              </dt>
              <dd class="tw-mt-1 tw-text-3xl tw-font-semibold tw-leading-9 tw-text-gray-900">
                {{ total[1] }}
              </dd>
            </dl>
          </div>
          <div class="tw-bg-gray-100 tw-px-3 tw-py-2">
            <div class="tw-leading-5">
              <span class="hover:text-purple-500 tw-font-medium tw-text-purple-600">
                {{ total[0] === activityType ? "Hide activities" : "Show activities" }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div v-if="activitiesResult" class="tw-b-r tw-m-2 tw-bg-gray-100 tw-p-2 tw-shadow">
        <div class="tw-flex tw-items-center tw-justify-between">
          <h2 class="tw-col-span-4 tw-font-medium">{{ activityType }}</h2>
          <RsPagination :result="activitiesResult" :page="page" :count="pageSize" @do-search="doSearch" />
        </div>
      </div>
      <div
        v-if="activitiesResult"
        class="tw-mx-2 tw-overflow-auto tw-rounded-lg tw-border-b tw-border-gray-200 tw-shadow"
      >
        <table class="tw-min-w-full tw-divide-y tw-divide-gray-200">
          <thead>
            <tr>
              <th
                class="tw-sticky tw-top-0 tw-bg-gray-100 tw-px-6 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-uppercase tw-leading-4 tw-tracking-wider tw-text-gray-500"
              >
                Case
              </th>
              <th
                class="tw-sticky tw-top-0 tw-bg-gray-100 tw-px-6 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-uppercase tw-leading-4 tw-tracking-wider tw-text-gray-500"
              >
                File Reference
              </th>
              <th
                class="tw-sticky tw-top-0 tw-bg-gray-100 tw-px-6 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-uppercase tw-leading-4 tw-tracking-wider tw-text-gray-500"
              >
                Date
              </th>
              <th
                class="tw-sticky tw-top-0 tw-bg-gray-100 tw-px-6 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-uppercase tw-leading-4 tw-tracking-wider tw-text-gray-500"
              >
                Quantity
              </th>
              <th
                class="tw-sticky tw-top-0 tw-bg-gray-100 tw-px-6 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-uppercase tw-leading-4 tw-tracking-wider tw-text-gray-500"
              >
                Origin
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(activity, index) in activitiesResult.items"
              :key="index"
              :class="index % 2 ? 'tw-bg-gray-100' : 'tw-bg-white'"
            >
              <td
                class="tw-whitespace-no-wrap tw-px-6 tw-py-2 tw-font-medium tw-leading-5 tw-text-gray-900 tw-underline hover:tw-opacity-75"
              >
                <router-link
                  :to="{
                    name: 'case',
                    params: { brand, casename: activity.caseReference },
                  }"
                >
                  {{ activity.caseReference }}
                </router-link>
              </td>
              <td class="tw-whitespace-no-wrap tw-px-6 tw-py-2 tw-leading-5 tw-underline hover:tw-opacity-75">
                <router-link
                  :to="{
                    name: 'casefile',
                    params: {
                      brand,
                      casename: activity.caseReference,
                      casefile: activity.fileReference,
                    },
                  }"
                >
                  {{ activity.fileReference }}
                </router-link>
              </td>
              <td class="tw-whitespace-no-wrap tw-px-6 tw-py-2 tw-leading-5">
                {{ fromUtc(activity.date) }}
              </td>
              <td class="tw-whitespace-no-wrap tw-px-6 tw-py-2 tw-leading-5">
                {{ activity.quantity }}
              </td>
              <td class="tw-whitespace-no-wrap tw-px-6 tw-py-2 tw-leading-5">
                {{ activity.origin }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
